import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MarketDialogueStatus,
  useDeleteMarketDialogueMutation,
  useGetMarketDialogueQuery,
  type MarketDialogueDto,
} from "../../autogen/bff-api";
import { LastChangedTag } from "../../common/editing/LastChangedTag/LastChangedTag";
import { SaveStatusBadge } from "../../common/editing/SaveStatusBadge/SaveStatusBadge";
import { isNotFoundError } from "../../common/errors/isNotFoundError";
import { useApiError } from "../../common/errors/useApiError";
import { flushPendingSaves, PendingSavesProvider, usePendingSavesRegistry } from "../../common/input/pendingSaves";
import { useWizardStepNavigation } from "../../common/wizard/useWizardStepNavigation";
import { WizardStepBar } from "../../common/wizard/WizardStepBar";
import { wizardStepPaths } from "../../common/wizard/wizardStepPaths";
import { StepNavigation } from "../../common/StepNavigation";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { useAppDispatch } from "../../common/redux/hooks";
import { urls } from "../../urls";
import { MarketDialogueActionsMenu } from "./MarketDialogueActionsMenu";
import { MarketDialogueTeamButton } from "./MarketDialogueTeamButton";
import { MarketDialogueAnswers } from "./MarketDialogueAnswers";
import { MarketDialogueConversation } from "./MarketDialogueConversation";
import { CHANGED_ELSEWHERE, type ChangedElsewhereRefusal } from "./changedElsewhere";
import { MarketDialogueDocuments } from "./MarketDialogueDocuments";
import { MarketDialogueForm, type MarketDialogueFormHandle, type MarketDialogueFormStep } from "./MarketDialogueForm";
import { MarketDialogueFormSection } from "./MarketDialogueFormSection";
import { CommentRoundNotice } from "./CommentRoundNotice";
import { MarketDialogueNeedNotice } from "./MarketDialogueNeedNotice";
import { MarketDialogueProcurementsNotice } from "./MarketDialogueProcurementsNotice";
import { MarketDialogueInvitations } from "./MarketDialogueInvitations";
import { MarketDialogueNoticeType } from "./MarketDialogueNoticeType";
import { MarketDialoguePublishPanel } from "./MarketDialoguePublishPanel";
import { MarketDialogueStatusTag } from "./MarketDialogueStatusTag";
import { StartCompetitionButton } from "./StartCompetitionButton";
import { firstTooLongQuestion } from "./questionLimits";
import { marketDialogueSteps, type MarketDialogueStep } from "./marketDialogueSteps";
import { MarketDialogueSummary } from "./MarketDialogueSummary";
import { useMarketDialoguePublish } from "./useMarketDialoguePublish";
import { fetchSettledMarketDialogue } from "./fetchSettledMarketDialogue";
import { MAX_QUESTION_LENGTH } from "./questionLimits";
import { DraftCard } from "../../common/layout/DraftCard";
import { MobileStepDrawer } from "../../common/wizard/MobileStepDrawer";

const formStepOf = (step: MarketDialogueStep): MarketDialogueFormStep | null =>
  step === "basis" || step === "questions" || step === "reachMarket" ? step : null;

export const MarketDialoguePage = () => {
  const { t } = useTranslation();
  const { marketDialogueId = "" } = useParams();
  const { pathname } = useLocation();
  const { data: dialogue, isLoading, isFetching, error, refetch } = useGetMarketDialogueQuery({ marketDialogueId });
  const pendingSaves = usePendingSavesRegistry();
  const [formLoadCount, setFormLoadCount] = useState(0);
  const [refusedAsChangedElsewhere, setRefusedAsChangedElsewhere] = useState<ChangedElsewhereRefusal | null>(null);
  const [reloadFailed, setReloadFailed] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "error">("idle");
  const [reportedTooLongQuestion, setReportedTooLongQuestion] = useState<number | null>();
  const [savedAt, setSavedAt] = useState<string>();
  const form = useRef<MarketDialogueFormHandle>(null);
  const stepLabel = (key: MarketDialogueStep) =>
    ({
      basis: t("Basis"),
      questions: t("Questions"),
      documents: t("Documents"),
      reachMarket: t("Reach the market"),
      summary: t("Summary"),
    })[key];
  const { currentStep, navigateToStep, navPropsFor } = useWizardStepNavigation({
    model: marketDialogueSteps,
    visibility: dialogue?.canEdit === true,
    stepPaths: wizardStepPaths(pathname, marketDialogueSteps.keys),
    stepLabel,
  });
  const step = currentStep ?? "basis";
  const dispatch = useAppDispatch();
  const publishing = useMarketDialoguePublish({
    marketDialogueId: dialogue?.id,
    pendingSaves,
    readLatest: () =>
      dispatch(fetchSettledMarketDialogue({ marketDialogueId }))
        .unwrap()
        .then(
          (data) => ({ data, isError: false }),
          () => ({ isError: true }),
        ),
    contentRevision: () => form.current?.contentRevision() ?? dialogue?.contentRevision ?? 0,
    onRefusedAsChangedElsewhere: () => setRefusedAsChangedElsewhere("publish"),
  });

  const reload = async () => {
    setReloadFailed(false);
    if ((await refetch()).isError) {
      setReloadFailed(true);
      return;
    }
    setRefusedAsChangedElsewhere(null);
    setSaveStatus("idle");
    setReportedTooLongQuestion(undefined);
    setFormLoadCount((count) => count + 1);
  };
  const title = dialogue?.title.trim() || t("New market dialogue");
  const tooLongQuestion =
    reportedTooLongQuestion === undefined ? firstTooLongQuestion(dialogue?.questions ?? []) : reportedTooLongQuestion;
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [deleteDialogue] = useDeleteMarketDialogueMutation();
  const backToList = () => navigate(urls.app.marketDialogues.viewAll);

  const confirmDelete = (id: string) =>
    confirmationModal.show({
      title: t("Delete the draft?"),
      description: t("The draft is permanently removed."),
      confirmButtonColor: "red",
      confirmButtonText: t("Delete") ?? "",
      onConfirmed: async () => {
        try {
          await flushPendingSaves(pendingSaves);
          await deleteDialogue({ marketDialogueId: id }).unwrap();
          backToList();
        } catch (e) {
          if (isNotFoundError(e)) return backToList();
          trigger(e);
          void refetch();
        }
      },
    });

  const publishPanel = (current: MarketDialogueDto) => (
    <MarketDialoguePublishPanel
      dialogue={current}
      publishing={publishing}
      refusedAsChangedElsewhere={refusedAsChangedElsewhere}
      onReload={reload}
      isReloading={isFetching}
      reloadFailed={reloadFailed}
      onGoToStep={current.canEdit ? navigateToStep : undefined}
      stepLabel={stepLabel}
    />
  );

  if (isLoading) return <Spinner />;
  if (!dialogue) {
    return <MarketDialogueLoadFailure error={error} isRetrying={isFetching} onRetry={() => void refetch()} />;
  }

  const stepNavigation = navPropsFor(step);
  const publishDisabledReason = (current: MarketDialogueDto): string | null => {
    if (refusedAsChangedElsewhere) return t("Reload the market dialogue before you publish.");
    if (tooLongQuestion !== null) {
      return t("Question {{number}} is longer than {{max}} characters. Shorten it before you publish.", {
        number: tooLongQuestion,
        max: MAX_QUESTION_LENGTH,
      });
    }
    if (saveStatus === "error") {
      return t("Your last change was not saved. Choose «Try again» beside «Could not save» before you publish.");
    }
    if (!current.canPublish) return t("Fill in all required fields to enable publishing");
    return null;
  };
  const Frame = dialogue.canEdit ? DraftCard : Box;

  return (
    <PendingSavesProvider registry={pendingSaves}>
      <Frame width="full" maxWidth="full" data-testid="market-dialogue-frame">
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "flex-start" }}
          gap={{ base: "3", md: "4" }}
        >
          <Box flex="1" minW="0">
            <Heading as="h1" size="md" width="full" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap" mb="2">
              {title}
            </Heading>
            <HStack spacing="1" wrap="wrap">
              {dialogue.status === MarketDialogueStatus.Draft ? (
                <Tag size="sm" colorScheme="teal" variant="subtle">
                  {t("Draft")}
                </Tag>
              ) : (
                <MarketDialogueStatusTag status={dialogue.status} announced={dialogue.announceOnDoffin} size="sm" />
              )}
              {dialogue.round.number > 1 && (
                <Tag size="sm" variant="outline" colorScheme="teal">
                  {t("Round {{number}}", { number: dialogue.round.number })}
                </Tag>
              )}
              {dialogue.canEdit && <LastChangedTag timestamp={latest(dialogue.modifiedAt, savedAt)} />}
              {dialogue.canEdit &&
                (tooLongQuestion !== null ? (
                  <Tag
                    as="button"
                    type="button"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => navigateToStep("questions")}
                  >
                    {t("Not saved – question {{number}} is too long", { number: tooLongQuestion })}
                  </Tag>
                ) : (
                  <>
                    <SaveStatusBadge status={saveStatus} variant="tag" />
                    {saveStatus === "error" && (
                      <Button size="sm" variant="link" colorScheme="teal" onClick={() => void form.current?.saveAgain()}>
                        {t("Try again")}
                      </Button>
                    )}
                  </>
                ))}
            </HStack>
          </Box>
          <HStack flexShrink={0} spacing="2" flexWrap="wrap" justify={{ base: "flex-start", md: "flex-end" }}>
            {dialogue.canStartCompetition && (
              <StartCompetitionButton marketDialogueId={dialogue.id} questions={dialogue.questions} />
            )}
            <MarketDialogueTeamButton marketDialogueId={dialogue.id} />
            <MarketDialogueActionsMenu onDelete={dialogue.canDelete ? () => confirmDelete(dialogue.id) : undefined} />
          </HStack>
        </Flex>
        {dialogue.needId && (
          <MarketDialogueNeedNotice needId={dialogue.needId} createdFromNeed={dialogue.createdFromNeed ?? false} />
        )}
        <MarketDialogueProcurementsNotice procurements={dialogue.procurements ?? []} />
        <CommentRoundNotice round={dialogue.round} dialogueId={dialogue.id} />
        {refusedAsChangedElsewhere === "save" && (
          <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start" mt="4">
            <AlertIcon />
            <Stack spacing="3" align="flex-start">
              <AlertDescription>{t(CHANGED_ELSEWHERE)}</AlertDescription>
              {reloadFailed && <Text color="smError">{t("Could not reload the market dialogue. Try again.")}</Text>}
              <Button size="sm" onClick={() => void reload()} isLoading={isFetching}>
                {t("Reload")}
              </Button>
            </Stack>
          </Alert>
        )}
        {dialogue.canEdit ? (
          <>
            <MobileStepDrawer
              steps={marketDialogueSteps.visibleSteps(true)}
              currentStep={step}
              navigateToStep={navigateToStep}
              stepLabel={stepLabel}
            />
            <Box py={{ base: "4", md: "8" }}>
              <WizardStepBar
                steps={marketDialogueSteps.keys}
                currentStep={step}
                navigateToStep={navigateToStep}
                stepLabel={stepLabel}
                stepShortLabel={stepLabel}
              />
            </Box>
            <Stack spacing="6" data-testid={`market-dialogue-step-${step}`}>
              <MarketDialogueForm
                key={`${dialogue.id}:${formLoadCount}`}
                ref={form}
                step={formStepOf(step)}
                dialogue={dialogue}
                changedElsewhere={refusedAsChangedElsewhere !== null}
                onChangedElsewhere={() => {
                  setSaveStatus("idle");
                  setRefusedAsChangedElsewhere("save");
                }}
                onSaving={() => setSaveStatus("saving")}
                onSaved={(saved) => {
                  setSavedAt(saved.modifiedAt);
                  setSaveStatus("idle");
                }}
                onSaveFailed={() => setSaveStatus("error")}
                onHeldBack={setReportedTooLongQuestion}
              />
              {step === "documents" && (
                <MarketDialogueFormSection heading={t("Documents")}>
                  <MarketDialogueDocuments dialogue={dialogue} refetch={() => void refetch()} />
                </MarketDialogueFormSection>
              )}
              {step === "reachMarket" && dialogue.announceOnDoffin && (
                <MarketDialogueFormSection heading={t("Announcement on Doffin")}>
                  <MarketDialogueNoticeType dialogue={dialogue} />
                </MarketDialogueFormSection>
              )}
              {step === "reachMarket" && <MarketDialogueInvitations dialogue={dialogue} />}
              {step === "summary" && (
                <>
                  <MarketDialogueSummary dialogue={dialogue} onEditStep={navigateToStep} />
                  {publishPanel(dialogue)}
                </>
              )}
            </Stack>
            {step === "summary" ? (
              <StepNavigation
                onPrevious={stepNavigation.previousStep}
                onNext={() => void publishing.publish()}
                nextIsAction
                nextLabel={dialogue.announceOnDoffin ? t("Publish on Doffin") : t("Open and send the invitations")}
                isNextLoading={publishing.isPublishing || publishing.isCheckingWhatDoffinGot}
                isNextDisabled={publishDisabledReason(dialogue) !== null}
                nextDisabledReason={publishDisabledReason(dialogue) ?? undefined}
              />
            ) : (
              <StepNavigation
                onPrevious={stepNavigation.previousStep}
                onNext={stepNavigation.nextStep}
                nextLabel={stepNavigation.nextStepLabel}
              />
            )}
          </>
        ) : (
          <Tabs colorScheme="teal" isLazy mt="6">
            <TabList>
              <Tab>{t("Setup")}</Tab>
              {receivesAnswers(dialogue.status) && <Tab>{t("Answers")}</Tab>}
              {receivesAnswers(dialogue.status) && <Tab>{t("Questions and sharing")}</Tab>}
            </TabList>
            <TabPanels>
              <TabPanel px="0">
                <Stack spacing="6">
                  <MarketDialogueSummary dialogue={dialogue} />
                  <MarketDialogueFormSection heading={t("Documents")}>
                    <MarketDialogueDocuments dialogue={dialogue} refetch={() => void refetch()} />
                  </MarketDialogueFormSection>
                  <MarketDialogueInvitations dialogue={dialogue} />
                  {publishPanel(dialogue)}
                </Stack>
              </TabPanel>
              {receivesAnswers(dialogue.status) && (
                <TabPanel px="0">
                  <MarketDialogueAnswers marketDialogueId={dialogue.id} questions={dialogue.questions} />
                </TabPanel>
              )}
              {receivesAnswers(dialogue.status) && (
                <TabPanel px="0">
                  <MarketDialogueConversation dialogue={dialogue} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        )}
      </Frame>
      {confirmationModal.modal}
    </PendingSavesProvider>
  );
};

const latest = (modifiedAt: string, savedAt: string | undefined) =>
  savedAt && new Date(savedAt) > new Date(modifiedAt) ? savedAt : modifiedAt;

const receivesAnswers = (status: MarketDialogueStatus) =>
  status === MarketDialogueStatus.Published || status === MarketDialogueStatus.AnswerDeadlinePassed;

const MarketDialogueLoadFailure = ({
  error,
  isRetrying,
  onRetry,
}: {
  error: unknown;
  isRetrying: boolean;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  const gone = isNotFoundError(error);
  return (
    <Alert status={gone ? "info" : "error"} rounded="md" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="3" align="flex-start">
        <AlertDescription>
          {gone ? t("This market dialogue no longer exists.") : t("Could not load the market dialogue.")}
        </AlertDescription>
        <HStack spacing="3">
          {!gone && (
            <Button size="sm" onClick={onRetry} isLoading={isRetrying}>
              {t("Try again")}
            </Button>
          )}
          <Button as={RouterLink} to={urls.app.marketDialogues.viewAll} size="sm" variant="link" colorScheme="teal">
            {t("Back to market dialogues")}
          </Button>
        </HStack>
      </Stack>
    </Alert>
  );
};
